import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { BarChart3, Coins, LineChart, Factory, Calculator, ArrowUpRight, Lock } from "lucide-react";
import { api, MODULES } from "@/lib/api";
import { PageHeader } from "@/components/dash";
import { useAuth } from "@/context/AuthContext";

const ICONS = { demand: BarChart3, ccop: Coins, prices: LineChart, capacity: Factory, capex: Calculator };
const DESC = {
  demand: "Regional & application demand, 4-yr history and forecast with CAGR",
  ccop: "Cost build-up, margins and raw-material/utility benchmarks by country",
  prices: "Monthly & daily chemical and utility price trends by region",
  capacity: "Plant-level capacity by company and region, 10-yr history + 5-yr pipeline",
  capex: "What-if capital cost calculator using scale-factor methodology",
};

export default function DashHome() {
  const { user } = useAuth();
  const cats = useQueries({ queries: MODULES.map((m) => ({ queryKey: ["catalog", m.id], queryFn: async () => (await api.get(`/catalog/${m.id}`)).data })) });
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={user ? `Welcome, ${user.name}` : "Solution Dashboards"} title={user ? "My Dashboards" : "Dashboards"} desc={user ? "Open any module you're subscribed to. Add products or regions from the panel at the top of each dashboard." : "Open any dashboard to explore a free sample product, then subscribe to the products and regions you need."} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {MODULES.map((m, i) => {
          const Icon = ICONS[m.id];
          const d = cats[i].data;
          const ent = d?.products.filter((p) => p.owned) || [];
          return (
            <div key={m.id} data-testid={`dash-module-card-${m.id}`} className="bg-[#0F172A]/80 border border-slate-800 rounded-lg p-6 fade-up hover:border-[#1074BA]/60 transition-colors" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-start justify-between">
                <span className="h-10 w-10 rounded-md bg-[#1074BA]/15 grid place-items-center"><Icon className="h-5 w-5 text-[#1074BA]" /></span>
                <span className="font-mono text-xs text-slate-500">{d ? `${ent.length}/${d.products.length} products` : "…"}</span>
              </div>
              <h3 className="text-lg font-bold mt-4">{m.label}</h3>
              <p className="text-sm text-slate-400 mt-1 min-h-[40px]">{DESC[m.id]}</p>
              <div className="flex flex-wrap gap-1.5 mt-4 min-h-[28px]">
                {ent.slice(0, 4).map((p) => <span key={p.name} className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-300">{p.name}</span>)}
                {ent.length > 4 && <span className="text-[11px] px-2 py-0.5 text-slate-500">+{ent.length - 4}</span>}
              </div>
              <div className="flex gap-2 mt-5">
                <Link to={m.path} data-testid={`dash-open-${m.id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#1074BA] hover:bg-[#0D629E] text-white text-sm font-medium">Open <ArrowUpRight className="h-4 w-4" /></Link>
                {d && ent.length < d.products.length && (
                  <Link to={m.path} data-testid={`dash-subscribe-${m.id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-slate-700 hover:border-[#F58B23] text-sm text-slate-300"><Lock className="h-3.5 w-3.5" /> Subscribe</Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
